import { measureLatency } from '../../../gctl-measure';
import {
  SuggestedUserMentionsFields,
  SuggestedUserMentions,
} from '../../../models/components/schemas/suggested-user-mentions/SuggestedUserMentions';
import {
  Select,
  NetworkError,
  deepCamelCase,
  searchQueryString,
  intoSnakeCase,
} from '../../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetAlbumVideoSuggestedUserMentionsFields = readonly SuggestedUserMentionsFields[];

/** Options for GET /albums/{album_id}/videos/{video_id}/suggested_user_mentions */
export type GetAlbumVideoSuggestedUserMentionsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /albums/{album_id}/videos/{video_id}/suggested_user_mentions */
  query?: {
    /**
     * The attribute by which to filter the results.
     *
     * Option descriptions:
     *  * `collaborator` - Return suggestions using collaborator comments as the source for generating the user list.
     *  * `viewer` - Return suggestions using viewer comments as the source for generating the user list.
     *
     */
    filter?: `collaborator` | `viewer`;

    /**
     * The page number of the results to show.
     */
    page?: number;

    /**
     * The password of the showcase or review link.
     */
    password?: string;

    /**
     * The number of user mention suggestions to return per page.
     */
    perPage?: number;

    /**
     * The search query to use to filter the results.
     */
    query?: string;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;

    /**
     * The ID of the user's active team owner.
     */
    teamOwnerId?: number;
  };
};

/** Result type for GET /albums/{album_id}/videos/{video_id}/suggested_user_mentions */
export type GetAlbumVideoSuggestedUserMentionsResult<Paths extends readonly string[]> = {
  /** The total number of results for GET /albums/{album_id}/videos/{video_id}/suggested_user_mentions */
  total: number;
  /** The current page */
  page: number;
  /** The number of results per page specified in the request */
  perPage: number;
  /** Available API endpoints for traversing the paginated data */
  paging: {
    /** The next page, if available */
    next: string | null;
    /** The previous page, if available */
    previous: string | null;
    /** The first page */
    first: string;
    /** The last page */
    last: string;
  };
  /** An array containing the current results */
  data: Select<SuggestedUserMentions, Paths>[];
};

/**
 * Get suggested users for mentions in comments
 * This method returns all the suggested users who can be mentioned in the comments of the specified video based on the user's search query. Please note that the comments used for generating the suggestions differ based on the source filter.
 */
export async function getAlbumVideoSuggestedUserMentions<
  Paths extends GetAlbumVideoSuggestedUserMentionsFields,
>({
  baseUrl,
  select,
  where: { albumId, videoId },
  query,
  ...options
}: GetAlbumVideoSuggestedUserMentionsOptions<Paths>): Promise<
  GetAlbumVideoSuggestedUserMentionsResult<Paths>
> {
  return measureLatency('getAlbumVideoSuggestedUserMentions', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/albums/${albumId}/videos/${videoId}/suggested_user_mentions?${searchQueryString(
        query,
      )}&fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetAlbumVideoSuggestedUserMentionsResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
